import React from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import * as defaultConst from '../../constants/default_constants'
import * as linkConst from '../../constants/link_constants'
import EditView from 'bstrap-react-mobx-admin/view/edit'
import SelectInput from '../input/select'
import TextInput from 'bstrap-react-mobx-admin/input/text'
import { Helmet } from 'react-helmet'
import { Table, Checkbox } from 'react-bootstrap'

const RightEditForm = observer(({ state }) => {
  const entity = state.cv.record
  const errors = state.cv.errors
  const updateField = state.cv.updateData.bind(state.cv)
  const disabled = !state.hasRight(defaultConst.ENTNAMES.RIGHTS, 'w')
  const rights = entity.get('user_access_rights')
  const allRightsView = [...rights, ...Object.keys(linkConst.LINKS_LIST)
    .filter(l => !rights.find(r => r.entity === l)).map(r => ({ entity: r, typ: '' }))]
    .sort((a, b) => (
      linkConst.LINKS_LIST[a.entity] && linkConst.LINKS_LIST[b.entity] &&
      linkConst.LINKS_LIST[a.entity].localeCompare(linkConst.LINKS_LIST[b.entity])) ||
      false)
  const allRChecked = allRightsView.every(r => r.typ === 'r')
  const allWChecked = allRightsView.every(r => r.typ === 'rw')

  const onInputChange = (e, t) => {
    const r = rights.find(r => r.entity === e)
    const change = { entity: e, typ: t === 'w' ? 'rw' : 'r' }

    if (!r) {
      rights.push(change)
    } else {
      if (t === 'w') {
        if (r.typ.includes('rw')) {
          change.typ = ''
        } else if (r.typ.includes('r') || r.typ === '') {
          change.typ = 'rw'
        }
      } else {
        if (r.typ.includes('rw') || r.typ === '') {
          change.typ = 'r'
        } else {
          change.typ = ''
        }
      }
      r.typ = change.typ
    }

    if (entity.has('changes')) {
      const changes = entity.get('changes')
      const changeLookup = changes.find(c => c.entity === change.entity)
      if (changeLookup) {
        changeLookup.typ = change.typ
      } else {
        changes.push(change)
      }
    } else {
      entity.set('changes', [change])
    }

    // for global validator to work
    updateField('_global_validator_run_', '')
  }

  const onAllSelect = t => {
    allRightsView.forEach(e => {
      if (t === 'w') {
        if (allWChecked) {
          onInputChange(e.entity, 'w')
        } else {
          if (e.typ !== 'rw') {
            onInputChange(e.entity, 'w')
          }
        }
      } else {
        if (allRChecked) {
          onInputChange(e.entity, 'r')
        } else {
          if (e.typ !== 'r') {
            onInputChange(e.entity, 'r')
          }
        }
      }
    })

    // for global validator to work
    updateField('_global_validator_run_', '')
  }

  return (
    <div className='row'>
      <Helmet title={state.__(linkConst.LINK_RIGHTS_TITLE).concat(defaultConst.HTML_DIVIDER) + (entity.has('uid')
        ? (entity.get('uid') || state.__(defaultConst.TITLE_EDIT))
        : state.__(defaultConst.TITLE_CREATE))} />
      <div className='col-xs-12 col-sm-6 col-lg-4'>
        <TextInput
          attr={'uid'}
          label={state.__('UID (email)')}
          record={entity}
          onChange={updateField}
          errors={errors}
          disabled={disabled || entity.get('id')} />

        <Table striped bordered condensed>
          <tbody>
            <tr>
              <th>{state.__('Entity')}</th>
              <th className={'uar-checkbox-field'}>R</th>
              <th className={'uar-checkbox-field'}>W</th>
            </tr>
            <tr>
              <td style={{ textAlign: 'right' }}><small>{state.__('Select all: ')}</small></td>
              <td className={'uar-checkbox-field'}><Checkbox checked={allRChecked} onChange={() => onAllSelect('r')} inline /></td>
              <td className={'uar-checkbox-field'}><Checkbox checked={allWChecked} onChange={() => onAllSelect('w')} inline /></td>
            </tr>
            {
              allRightsView.map(r => (
                <tr key={r.entity}>
                  <td>{state.__(linkConst.LINKS_LIST[r.entity] || r.entity)}</td>
                  <td className={'uar-checkbox-field'}>
                    <Checkbox
                      checked={r.typ.includes('r') && !r.typ.includes('w')}
                      onChange={() => onInputChange(r.entity, 'r')} inline />
                  </td>
                  <td className={'uar-checkbox-field'}>
                    <Checkbox
                      checked={r.typ.includes('w')}
                      onChange={() => onInputChange(r.entity, 'w')} inline />
                  </td>
                </tr>
              ))
            }
          </tbody>
        </Table>
      </div>
    </div>
  )
})

RightEditForm.propTypes = {
  state: PropTypes.object.isRequired
}

const EntityRightEditForm = observer(({ state }) => {
  const entity = state.cv.record
  const errors = state.cv.errors
  const updateField = state.cv.updateData.bind(state.cv)
  const disabled = !state.hasRight(defaultConst.ENTNAMES.RIGHTS, 'w')

  if (!state.options.has('entities')) {
    state.options.set('entities', observable.array(Object.keys(linkConst.LINKS_LIST).map(l => ({ value: l, text: linkConst.LINKS_LIST[l] }))))
  }

  const allRights = state.options.get('access_rights')
  const uniqueEmails = state.options.get('rights')

  const allRChecked = uniqueEmails.every(e => {
    const c = allRights.find(r => r.uid === e.uid && r.entity === entity.get('entity'))
    return c ? c.typ === 'r' : false
  })
  const allWChecked = uniqueEmails.every(e => {
    const c = allRights.find(r => r.uid === e.uid && r.entity === entity.get('entity'))
    return c ? c.typ === 'rw' : false
  })

  const onInputChange = (e, t) => {
    const r = allRights.find(r => r.entity === entity.get('entity') && r.uid === e)
    const user = uniqueEmails.find(r => r.uid === e)
    const change = { uid: e, entity: entity.get('entity'), user_access_id: user.id, updated_at: new Date() }

    if (!r) {
      change.typ = t === 'w' ? 'rw' : 'r'
      allRights.push(change)
    } else {
      change.id = r.id
      if (t === 'w') {
        if (r.typ.includes('rw')) {
          change.typ = ''
        } else if (r.typ.includes('r') || r.typ === '') {
          change.typ = 'rw'
        }
      } else {
        if (r.typ.includes('rw') || r.typ === '') {
          change.typ = 'r'
        } else {
          change.typ = ''
        }
      }
      r.typ = change.typ
    }

    if (entity.has('changes')) {
      const changes = entity.get('changes')
      const changeLookup = changes.find(c => c.uid === change.uid && c.entity === change.entity)

      if (changeLookup) {
        changeLookup.typ = change.typ
      } else {
        changes.push(change)
      }
    } else {
      entity.set('changes', [change])
    }

    // for global validator to work
    updateField('_global_validator_run_', '')
  }

  const onAllSelect = t => {
    uniqueEmails.forEach(e => {
      if (t === 'w') {
        if (allWChecked) {
          onInputChange(e.uid, 'w')
        } else {
          const r = allRights.find(r => r.entity === entity.get('entity') && r.uid === e.uid)
          if (r) {
            if (r.typ !== 'rw') {
              onInputChange(e.uid, 'w')
            }
          } else {
            onInputChange(e.uid, 'w')
          }
        }
      } else {
        if (allRChecked) {
          onInputChange(e.uid, 'r')
        } else {
          const r = allRights.find(r => r.entity === entity.get('entity') && r.uid === e.uid)
          if (r) {
            if (r.typ !== 'r') {
              onInputChange(e.uid, 'r')
            }
          } else {
            onInputChange(e.uid, 'r')
          }
        }
      }
    })

    // for global validator to work
    updateField('_global_validator_run_', '')
  }

  const itemName = entity.has('entity') &&
    state.options.get('entities') &&
    state.options.get('entities').find(f => f.value === entity.get('entity'))

  return (
    <div className='row'>
      <Helmet title={state.__(linkConst.LINK_RIGHTS_TITLE).concat(defaultConst.HTML_DIVIDER) + (entity.has('entity')
        ? ((itemName && itemName.text) || state.__(defaultConst.TITLE_EDIT))
        : state.__(defaultConst.TITLE_CREATE))} />
      <div className='col-xs-12 col-sm-6 col-lg-4'>
        <SelectInput
          label={state.__('Entity')}
          attr={'entity'}
          record={entity}
          labelattr='text'
          valueattr='value'
          onChange={updateField}
          optionsrecord={state.options}
          optionsattr={'entities'}
          errors={errors}
          disabled={disabled} />

        {entity.has('entity') && entity.get('entity') &&
          <p className='alert alert-warning'>Beware, that <strong>all</strong> changes are saved.</p>
        }

        <Table striped bordered condensed>
          <tbody>
            <tr>
              <th>{state.__('UID (email)')}</th>
              <th className={'uar-checkbox-field'}>R</th>
              <th className={'uar-checkbox-field'}>W</th>
            </tr>
            <tr>
              <td style={{ textAlign: 'right' }}><small>{state.__('Select all: ')}</small></td>
              <td className={'uar-checkbox-field'}><Checkbox checked={allRChecked} onChange={() => onAllSelect('r')} inline /></td>
              <td className={'uar-checkbox-field'}><Checkbox checked={allWChecked} onChange={() => onAllSelect('w')} inline /></td>
            </tr>
            {entity.get('entity') && uniqueEmails
              ? uniqueEmails.map(email =>
                <tr key={email.uid}>
                  <td>{state.__(email.uid)}</td>
                  <td className={'uar-checkbox-field'}>
                    <Checkbox
                      checked={!!allRights.find(i =>
                        i.uid === email.uid &&
                        i.entity === entity.get('entity') &&
                        i.typ === 'r')}
                      onChange={() => onInputChange(email.uid, 'r')} inline />
                  </td>
                  <td className={'uar-checkbox-field'}>
                    <Checkbox
                      checked={!!allRights.find(i =>
                        i.uid === email.uid &&
                        i.entity === entity.get('entity') &&
                        i.typ === 'rw')}
                      onChange={() => onInputChange(email.uid, 'w')} inline />
                  </td>
                </tr>)
              : <tr><td colSpan={3} style={{ textAlign: 'center' }}><em>
                {state.__('- No items -')}
              </em></td></tr>
            }
          </tbody>
        </Table>
      </div>
    </div>
  )
})

EntityRightEditForm.propTypes = {
  state: PropTypes.object.isRequired
}

const RightEditView = ({ store }) => {
  if (!store.hasRight(defaultConst.ENTNAMES.RIGHTS)) {
    return <h1>{store.__('insufficient rights')}</h1>
  }

  const isEntityEdit = (store.router.queryParams &&
    store.router.queryParams.hasOwnProperty('type') &&
    store.router.queryParams.type === 'entity')

  return (
    <EditView store={store.cv} onReturn2list={store.onReturn2list.bind(store)}
      enabled={store.hasRight(defaultConst.ENTNAMES.RIGHTS, 'w')} >
      {isEntityEdit ? <EntityRightEditForm state={store} /> : <RightEditForm state={store} />}
    </EditView>
  )
}

RightEditView.propTypes = {
  store: PropTypes.object.isRequired
}

export default RightEditView
